'use client';

import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';

interface ESPMapping {
  platform: string;
  firstName: string;
  lastName: string;
  email: string;
  unsubscribe: string;
  viewInBrowser: string;
  conditionalSyntax: string;
  loopSyntax: string;
  notes: string;
}

const ESP_DATA: ESPMapping[] = [
  {
    platform: 'PostBrix (Handlebars)',
    firstName: '{{firstName}}',
    lastName: '{{lastName}}',
    email: '{{email}}',
    unsubscribe: '{{unsubscribeUrl}}',
    viewInBrowser: '{{viewInBrowserUrl}}',
    conditionalSyntax: '{{#if isVip}}...{{/if}}',
    loopSyntax: '{{#each items}}...{{/each}}',
    notes: 'Native builder syntax preserved in dynamic HTML export.',
  },
  {
    platform: 'Mailchimp',
    firstName: '*|FNAME|*',
    lastName: '*|LNAME|*',
    email: '*|EMAIL|*',
    unsubscribe: '*|UNSUB|*',
    viewInBrowser: '*|ARCHIVE|*',
    conditionalSyntax: '*|IF:VIP=yes|*...*|END:IF|*',
    loopSyntax: 'Not supported in standard merge tags (requires Mailchimp feed or custom API)',
    notes: 'Must use pipe-delimited merge tags. Replace tags after pasting custom HTML.',
  },
  {
    platform: 'SendGrid',
    firstName: '{{firstName}}',
    lastName: '{{lastName}}',
    email: '{{email}}',
    unsubscribe: '{{{unsubscribe}}}',
    viewInBrowser: '{{Weblink}}',
    conditionalSyntax: '{{#if isVip}}...{{/if}}',
    loopSyntax: '{{#each items}}...{{/each}}',
    notes: 'Dynamic Templates support Handlebars directly. Unsubscribe requires triple braces {{{unsubscribe}}}.',
  },
  {
    platform: 'Klaviyo',
    firstName: '{{ person.first_name }}',
    lastName: '{{ person.last_name }}',
    email: '{{ person.email }}',
    unsubscribe: '{% unsubscribe_link %}',
    viewInBrowser: '{% web_view %}',
    conditionalSyntax: '{% if person.vip %}...{% endif %}',
    loopSyntax: '{% for item in event.Items %}...{% endfor %}',
    notes: 'Uses Django/Liquid template engine. Profile fields live under person.*.',
  },
  {
    platform: 'Brevo (Sendinblue)',
    firstName: '{{ contact.FIRSTNAME }}',
    lastName: '{{ contact.LASTNAME }}',
    email: '{{ contact.EMAIL }}',
    unsubscribe: '{{ unsubscribe }}',
    viewInBrowser: '{{ mirror }}',
    conditionalSyntax: '{% if contact.VIP == "true" %}...{% endif %}',
    loopSyntax: '{% for item in params.items %}...{% endfor %}',
    notes: 'Contact attributes must be uppercase (e.g. FIRSTNAME).',
  },
  {
    platform: 'HubSpot',
    firstName: '{{ contact.firstname }}',
    lastName: '{{ contact.lastname }}',
    email: '{{ contact.email }}',
    unsubscribe: '{{ unsubscribe_link }}',
    viewInBrowser: '{{ view_as_page_url }}',
    conditionalSyntax: '{% if contact.is_vip %}...{% endif %}',
    loopSyntax: '{% for item in custom_object %}...{% endfor %}',
    notes: 'Uses HubL. Ensure CAN-SPAM address tokens are placed in your footer.',
  },
  {
    platform: 'Postmark',
    firstName: '{{firstName}}',
    lastName: '{{lastName}}',
    email: '{{email}}',
    unsubscribe: '{{{pm:unsubscribe}}}',
    viewInBrowser: 'Not supported natively',
    conditionalSyntax: '{{#isVip}}...{{/isVip}}',
    loopSyntax: '{{#items}}...{{/items}}',
    notes: 'Supports Mustache syntax mapped to TemplateModel JSON properties.',
  },
  {
    platform: 'Customer.io',
    firstName: '{{ customer.first_name }}',
    lastName: '{{ customer.last_name }}',
    email: '{{ customer.email }}',
    unsubscribe: '{% unsubscribe_url %}',
    viewInBrowser: '{{ message.view_in_browser_url }}',
    conditionalSyntax: '{% if customer.plan == "pro" %}...{% endif %}',
    loopSyntax: '{% for item in event.items %}...{% endfor %}',
    notes: 'Liquid syntax. Event data is accessible under event.* and customer data under customer.*.',
  },
  {
    platform: 'ActiveCampaign',
    firstName: '%FIRSTNAME%',
    lastName: '%LASTNAME%',
    email: '%EMAIL%',
    unsubscribe: '%UNSUBSCRIBELINK%',
    viewInBrowser: '%WEBCOPY%',
    conditionalSyntax: 'Conditional blocks configured in UI',
    loopSyntax: 'Requires custom ecommerce integration',
    notes: 'Percent-delimited tags. Ensure mandatory unsubscribe and address tags are present.',
  },
  {
    platform: 'Mailgun',
    firstName: '%recipient.first_name%',
    lastName: '%recipient.last_name%',
    email: '%recipient.email%',
    unsubscribe: '%tag_unsubscribe_url%',
    viewInBrowser: 'Not supported natively',
    conditionalSyntax: 'Handled via Mailgun template engine or backend render',
    loopSyntax: 'Handled via backend render or Mailgun Handlebars templates',
    notes: 'Supports batch sending with recipient-variables JSON payload.',
  },
];

export function DocsSyntaxTable() {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('All');
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const copyValue = (val: string) => {
    void navigator.clipboard.writeText(val);
    setCopiedText(val);
    setTimeout(() => setCopiedText(null), 1500);
  };

  const filteredData =
    selectedPlatform === 'All'
      ? ESP_DATA
      : ESP_DATA.filter((d) => d.platform.toLowerCase().includes(selectedPlatform.toLowerCase()));

  return (
    <div className="my-8 rounded-2xl border border-pb-slate-200 dark:border-pb-white/[0.08] bg-pb-white dark:bg-[#0b1120] shadow-sm overflow-hidden">
      {/* Platform Selector Filter */}
      <div className="border-b border-pb-slate-200 dark:border-pb-white/[0.08] bg-pb-slate-50/80 dark:bg-pb-white/[0.02] p-5 sm:flex sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-bold text-pb-slate-900 dark:text-pb-white">
            ESP Merge Tag Translation Engine
          </h4>
          <p className="text-xs text-pb-slate-500 dark:text-pb-slate-400 mt-0.5">
            Select your delivery platform to see the required syntax for personalized fields.
          </p>
        </div>

        <div className="mt-3 sm:mt-0">
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            className="rounded-xl border border-pb-slate-200 dark:border-pb-white/[0.1] bg-pb-white dark:bg-[#0b1329] px-3.5 py-1.5 text-xs font-semibold text-pb-slate-900 dark:text-pb-white focus:outline-none focus:border-pb-blue-500"
          >
            <option value="All">Show All Platforms (10)</option>
            {ESP_DATA.map((d) => (
              <option key={d.platform} value={d.platform}>
                {d.platform}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="custom-scrollbar overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="border-b border-pb-slate-200 dark:border-pb-white/[0.08] bg-pb-slate-50/90 dark:bg-pb-white/[0.03] font-semibold text-[11px] uppercase tracking-wider text-pb-slate-700 dark:text-pb-slate-300">
              <th className="p-3.5 pl-4">Platform</th>
              <th className="p-3.5">First Name</th>
              <th className="p-3.5">Email</th>
              <th className="p-3.5">Unsubscribe Tag</th>
              <th className="p-3.5">Conditional Logic</th>
              <th className="p-3.5 pr-4">Delivery Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-pb-slate-200/60 dark:divide-pb-white/[0.05]">
            {filteredData.map((row) => (
              <tr
                key={row.platform}
                className="hover:bg-pb-slate-50/70 dark:hover:bg-pb-white/[0.02] transition-colors text-pb-slate-600 dark:text-pb-slate-300"
              >
                <td className="p-3.5 pl-4 font-semibold text-pb-slate-900 dark:text-pb-white whitespace-nowrap">
                  {row.platform}
                </td>
                <td className="p-3.5">
                  <button
                    onClick={() => copyValue(row.firstName)}
                    title="Click to copy"
                    className="inline-flex items-center gap-1 font-mono rounded bg-pb-slate-100 dark:bg-pb-white/[0.06] px-2 py-0.5 text-pb-blue-600 dark:text-pb-blue-400 hover:bg-pb-blue-50 dark:hover:bg-pb-blue-500/15 transition-colors cursor-pointer"
                  >
                    <span>{row.firstName}</span>
                    {copiedText === row.firstName ? (
                      <Check size={11} className="text-emerald-500" />
                    ) : (
                      <Copy size={11} className="opacity-40" />
                    )}
                  </button>
                </td>
                <td className="p-3.5 font-mono text-[11.5px] text-pb-slate-600 dark:text-pb-slate-300 whitespace-nowrap">
                  {row.email}
                </td>
                <td className="p-3.5">
                  <button
                    onClick={() => copyValue(row.unsubscribe)}
                    title="Click to copy"
                    className="inline-flex items-center gap-1 font-mono rounded bg-pb-slate-100 dark:bg-pb-white/[0.06] px-2 py-0.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/15 transition-colors cursor-pointer"
                  >
                    <span>{row.unsubscribe}</span>
                    {copiedText === row.unsubscribe ? (
                      <Check size={11} className="text-emerald-500" />
                    ) : (
                      <Copy size={11} className="opacity-40" />
                    )}
                  </button>
                </td>
                <td
                  className="p-3.5 font-mono text-[11.5px] text-pb-slate-600 dark:text-pb-slate-300 max-w-[200px] truncate"
                  title={row.conditionalSyntax}
                >
                  {row.conditionalSyntax}
                </td>
                <td className="p-3.5 pr-4 text-xs text-pb-slate-500 dark:text-pb-slate-400 min-w-[220px]">
                  {row.notes}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
